
import React from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { GraduationCap, Award, Users, Book } from 'lucide-react';
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";

const EducationSection = () => {
  return (
    <section id="education" className="py-16 px-6 bg-gradient-to-br from-sage-50 via-white to-warm-50">
      <div className="container max-w-5xl mx-auto">
        <div className="mb-12 text-center">
          <h2 className="text-sage-800 mb-2">Education</h2>
          <div className="w-24 h-1 bg-sage-300 mx-auto"></div>
        </div>
        
        <Card className="border-none shadow-lg overflow-hidden">
          <div className="bg-sage-700 p-6 text-white flex items-center justify-center">
            <GraduationCap className="w-8 h-8 mr-3" />
            <h3 className="text-2xl font-serif">Bachelor of Science in Psychology</h3>
          </div>
          
          <CardContent className="p-6 md:p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className="flex flex-col md:flex-row justify-between mb-6">
                  <div>
                    <h4 className="text-xl font-medium text-sage-800">Francis Marion University</h4>
                    <p className="text-sage-600">Collateral: Sociology & Anthropology</p>
                    <div className="mt-2">
                      <Popover>
                        <PopoverTrigger asChild>
                          <p className="text-sage-700 border-b border-dotted border-sage-400 inline-flex items-center cursor-pointer">
                            Concentration: Forensic Anthropology 
                            <Book className="ml-1 h-4 w-4" />
                          </p>
                        </PopoverTrigger>
                        <PopoverContent className="w-80 p-4">
                          <div className="space-y-2">
                            <h5 className="font-medium text-sage-800">Forensic Anthropology Focus</h5>
                            <p className="text-sm text-gray-700">
                              Studied human skeletal analysis, forensic identification, and the application 
                              of anthropological methods in legal contexts.
                            </p>
                          </div>
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>
                  <p className="text-sage-700 font-medium mt-2 md:mt-0">Class of 2023</p>
                </div>
                
                <div className="space-y-6 mt-4">
                  <div className="flex gap-4">
                    <div className="bg-sage-100 p-3 rounded-full h-fit">
                      <Award className="w-6 h-6 text-sage-600" />
                    </div>
                    <div>
                      <h4 className="text-lg font-medium text-sage-700 mb-2">Activities & Involvement</h4>
                      <ul className="space-y-2">
                        <li className="flex items-start">
                          <span className="mr-2 mt-1.5 h-1.5 w-1.5 rounded-full bg-sage-400 flex-shrink-0"></span>
                          <span className="text-gray-700">Boys & Girls Club Outreach Program</span>
                        </li>
                        <li className="flex items-start">
                          <span className="mr-2 mt-1.5 h-1.5 w-1.5 rounded-full bg-sage-400 flex-shrink-0"></span>
                          <span className="text-gray-700">Recital Organizer for Dance Programs</span>
                        </li>
                        <li className="flex items-start">
                          <span className="mr-2 mt-1.5 h-1.5 w-1.5 rounded-full bg-sage-400 flex-shrink-0"></span>
                          <span className="text-gray-700">Peer Mentor for Incoming Psychology Students</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                  
                  <div className="flex gap-4">
                    <div className="bg-sage-100 p-3 rounded-full h-fit">
                      <Users className="w-6 h-6 text-sage-600" />
                    </div>
                    <div>
                      <h4 className="text-lg font-medium text-sage-700 mb-2">Key Learning Areas</h4>
                      <div className="flex flex-wrap gap-2">
                        <span className="bg-sage-100 text-sage-800 px-3 py-1 rounded-full text-sm">Behavioral Psychology</span>
                        <span className="bg-sage-100 text-sage-800 px-3 py-1 rounded-full text-sm">Social Dynamics</span>
                        <span className="bg-sage-100 text-sage-800 px-3 py-1 rounded-full text-sm">Cultural Anthropology</span>
                        <span className="bg-sage-100 text-sage-800 px-3 py-1 rounded-full text-sm">Research Methods</span>
                        <span className="bg-sage-100 text-sage-800 px-3 py-1 rounded-full text-sm">Ethical Practice</span>
                        <span className="bg-sage-100 text-sage-800 px-3 py-1 rounded-full text-sm">Group Psychology</span>
                        <span className="bg-warm-100 text-warm-800 px-3 py-1 rounded-full text-sm">Forensic Analysis</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center justify-center">
                <div className="rounded-lg overflow-hidden shadow-lg border-4 border-sage-200 w-full max-w-sm transform rotate-2 hover:rotate-0 transition-transform duration-300">
                  <AspectRatio ratio={1/1.2} className="bg-sage-50">
                    <img 
                      src="/lovable-uploads/52f21062-24fb-4bd3-bbbc-21bd1d2e8c73.png" 
                      alt="Payton Hunter at graduation with diploma" 
                      className="object-cover w-full h-full"
                    />
                  </AspectRatio>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};

export default EducationSection;
